/**
 * The `AppStore` (Deliverables A + B + C) — the observable L3 contract surface,
 * widened to CONTRACT_VERSION 0.2.0.
 *
 * It wraps Agent 3's real L2 model (`@progression/model`: `reduce` / `replay` /
 * `createInitialState` / `exposure` / `lane`) and the L1 kernel behind the §6.3
 * `Store` (preserved) plus the additive Phase 2 surface (`contract2.ts`). The
 * boundary stays strict:
 *   - commands carry intent; the Store maps them to events; the model reducer
 *     recomputes state; no verdict is ever stored;
 *   - queries are pure reads — `verdictOf` / `validityUnder` call the kernel with a
 *     config derived from the current state and nothing else (INV-CORE), so a
 *     verdict can never drift from the config it was derived under (INV-NOEQUIV);
 *   - the canonical, ownership-grade log is the §6.2 `Event[]` (`exportLog`); the
 *     additive Phase 2 app-events ride a parallel app-log and never feed the
 *     kernel (INV-ADD-NOT-ALTER).
 *
 * History (undo/redo) is pure event sourcing: the app-log is kept as a list of
 * command *groups*; the cursor moves over them and the state is re-derived from
 * the active prefix on every change.
 */
import { defaultConfig, importEdge, evaluate, activate, challenge, applyEdgeToProfile, } from '@progression/core';
import { replay as modelReplay, exposure as modelExposure } from '@progression/model';
import { isModelEvent } from './contract2.js';
import { commandToEvents } from './mapping.js';
import { CONTRACT_VERSION } from './version.js';
import { toJSON, fromJSON, toReport } from './exporters.js';
const EMPTY_EXT = Object.freeze({
    edgeModal: Object.freeze(new Map()),
});
function foldExt(events) {
    const edgeModal = new Map();
    for (const e of events) {
        if (e.t === 'edge-modal-set')
            edgeModal.set(e.edgeId, e.m);
    }
    return Object.freeze({ edgeModal: Object.freeze(edgeModal) });
}
/** A computation record is well-formed enough to log iff it has a string id and claimRef. */
function wellFormedRecord(r) {
    return (r !== null &&
        typeof r === 'object' &&
        typeof r.id === 'string' &&
        typeof r.claimRef === 'string');
}
/**
 * Import-boundary sanitizer (Deliverable D; reinforces F4/F14). A log arriving
 * from disk/transport may contain a malformed `computation-recorded` record (or
 * other junk). We drop only the *malformed* entries and keep the rest, so an
 * untrusted snapshot can never brick a load. Unknown event *types* are left in
 * place — the model reducer already ignores them safely at runtime — but a
 * structurally-broken evidence record is removed here before it reaches the fold.
 */
function sanitizeForImport(events) {
    return events.filter((e) => {
        if (e !== null && typeof e === 'object' && e.t === 'computation-recorded') {
            return wellFormedRecord(e.record);
        }
        return e !== null && typeof e === 'object' && typeof e.t === 'string';
    });
}
/**
 * The active `Config` for a state: the adopted-formal base (`defaultConfig`) with
 * every imported edge composed in. Only `constraint` edges change it; the other
 * edge kinds are inert (the kernel guarantees this), which is why importing a
 * `resource`/`heuristic` edge changes no verdict.
 */
function configFor(state) {
    let config = defaultConfig(state.statusMap);
    for (const edge of state.edges)
        config = importEdge(edge.kind, edge.externalConstraintId, config);
    return config;
}
/** Apply a frame (activate/suspend) to a config purely — `frames are configs`. */
function applyFrame(base, frame) {
    let config = base;
    for (const id of frame.activate ?? [])
        config = activate(id, config);
    for (const id of frame.suspend ?? [])
        config = challenge(id, config);
    return config;
}
/**
 * Create an `AppStore`. Optionally seed it with a starting event log (used by the
 * fixture-seeded mock); each seed event becomes its own undoable step.
 */
export function createStore(seed = []) {
    // The application log as command groups; the cursor is `groups.length`.
    let groups = seed.map((e) => [e]);
    let redo = [];
    // Derived caches, recomputed from the active prefix on every change.
    let modelState = modelReplay([]);
    let ext = EMPTY_EXT;
    function recompute() {
        const active = groups.flat();
        modelState = modelReplay(active.filter(isModelEvent));
        ext = foldExt(active);
    }
    recompute();
    const listeners = new Set();
    function emit() {
        for (const listener of listeners)
            listener(modelState);
    }
    function pushGroup(events) {
        if (events.length === 0)
            return; // no-op commands (e.g. an empty frame) add nothing
        groups.push([...events]);
        redo = []; // a new action invalidates the redo stack
        recompute();
        emit();
    }
    function undo() {
        const last = groups.pop();
        if (last === undefined)
            return;
        redo.push(last);
        recompute();
        emit();
    }
    function redoOne() {
        const next = redo.pop();
        if (next === undefined)
            return;
        groups.push(next);
        recompute();
        emit();
    }
    function requireClaim(claimId) {
        const claim = modelState.claims.get(claimId);
        if (claim === undefined)
            throw new Error(`@progression/app: no claim '${claimId}' in state`);
        return claim;
    }
    const store = {
        // --- commands (Deliverable A) ---
        dispatch(command) {
            // State-dependent commands resolved here (they need the current log/state);
            // everything else is a pure mapping handled by `commandToEvents`.
            if (command.c === 'undo')
                return undo();
            if (command.c === 'redo')
                return redoOne();
            if (command.c === 'apply-edge-modal') {
                // Real modal-on-edges: the kernel computes the edge's effect on the claim's
                // profile; the result is recorded as a `profile-promoted` fact (INV-CORE).
                const claim = requireClaim(command.claimId);
                const to = applyEdgeToProfile(command.edgeKind, claim.profile);
                return pushGroup([{ t: 'profile-promoted', claimId: command.claimId, to }]);
            }
            pushGroup(commandToEvents(command));
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        replay(events) {
            groups = sanitizeForImport(events).map((e) => [e]);
            redo = [];
            recompute();
            emit();
        },
        exportLog() {
            // Canonical: exactly the §6.2 events in effect (== modelState.history).
            return modelState.history;
        },
        // --- queries (Deliverable B) — pure reads, state left unchanged ---
        getState() {
            return modelState;
        },
        verdictOf(claimId) {
            // INV-CORE: the verdict is K(claim, config) and nothing else.
            return evaluate(requireClaim(claimId), configFor(modelState));
        },
        validityUnder(claimId, frame) {
            // Framing-relative validity: evaluate under a frame-transformed config, with
            // no mutation of the store (a pure hypothetical).
            return evaluate(requireClaim(claimId), applyFrame(configFor(modelState), frame));
        },
        modalEffect(claimId, edgeKind) {
            // The kernel's edge→profile modal effect; pure, no mutation (the read-only
            // counterpart of the `apply-edge-modal` command).
            return applyEdgeToProfile(edgeKind, requireClaim(claimId).profile);
        },
        exposure(claimId) {
            return modelExposure(modelState, claimId);
        },
        evidenceOf(claimId) {
            // A pure read of the model's evidence map (populated by `computation-recorded`).
            // Evidence informs the Machine lane but is NEVER a verdict (INV-CORE) — verdicts
            // come only from `verdictOf`.
            return modelState.evidence?.get(claimId) ?? Object.freeze([]);
        },
        systemClaims() {
            return Object.freeze([...modelState.integrity.values()]);
        },
        lanesOf(systemClaimId) {
            return modelState.integrity.get(systemClaimId)?.lanes;
        },
        edgeModal(edgeId) {
            return ext.edgeModal.get(edgeId);
        },
        registeredPacks() {
            // Domain-pack provenance is owned by the model (added additively via
            // `module-registered`); the ids are exactly the registered module ids.
            return Object.freeze([...(modelState.modules?.keys() ?? [])]);
        },
        historyPosition() {
            return Object.freeze({
                applied: groups.length,
                undone: redo.length,
                canUndo: groups.length > 0,
                canRedo: redo.length > 0,
                events: groups.reduce((n, g) => n + g.length, 0),
            });
        },
        contractVersion() {
            return CONTRACT_VERSION;
        },
        // --- export / import (Deliverable C) ---
        exportAppLog() {
            return Object.freeze(groups.flat());
        },
        importAppLog(events) {
            groups = sanitizeForImport(events).map((e) => [e]);
            redo = [];
            recompute();
            emit();
        },
        exportJSON() {
            return toJSON(groups.flat());
        },
        importJSON(json) {
            this.importAppLog(fromJSON(json));
        },
        exportReport() {
            const hp = this.historyPosition();
            return toReport(modelState, (id) => this.verdictOf(id), {
                packs: [...(modelState.modules?.keys() ?? [])],
                history: `${hp.applied} applied, ${hp.undone} undone (${hp.events} events)`,
            });
        },
    };
    return store;
}
//# sourceMappingURL=store.js.map